"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { ConfiguratorInput } from "@components/features/configurator/types";

export interface UrlConfiguratorState {
  values: Record<string, number>;
  setValue: (id: string, value: number) => void;
  resetValues: () => void;
  appliedFromUrl: boolean;
  buildUrl: (
    basePath: string,
    extraParams?: Record<string, string | number | undefined>,
    hash?: string
  ) => string;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function useUrlConfig(inputs: ConfiguratorInput[]): UrlConfiguratorState {
  if (!Array.isArray(inputs) || inputs.length === 0) {
    throw new Error("useUrlConfig: `inputs` must be a non-empty array.");
  }

  const defaults = useMemo(
    () => Object.fromEntries(inputs.map((input) => [input.id, input.default])),
    [inputs]
  );

  const [values, setValues] = useState<Record<string, number>>(defaults);
  const [appliedFromUrl, setAppliedFromUrl] = useState(false);

  useEffect(() => {
    setValues(defaults);
  }, [defaults]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const nextValues = { ...defaults };
    let hasParamValue = false;

    inputs.forEach((input) => {
      const raw = params.get(input.id);
      if (raw === null) {
        return;
      }

      const parsed = Number(raw);
      if (Number.isNaN(parsed)) {
        return;
      }

      hasParamValue = true;
      nextValues[input.id] = clamp(parsed, input.min, input.max);
    });

    if (hasParamValue) {
      setValues(nextValues);
      setAppliedFromUrl(true);
    }
  }, [defaults, inputs]);

  const setValue = useCallback((id: string, value: number) => {
    setValues((prev) => ({
      ...prev,
      [id]: value,
    }));
  }, []);

  const resetValues = useCallback(() => {
    setValues(defaults);
  }, [defaults]);

  const buildUrl = useCallback(
    (
      basePath: string,
      extraParams: Record<string, string | number | undefined> = {},
      hash?: string
    ) => {
      if (typeof window === "undefined") {
        throw new Error("useUrlConfig: buildUrl can only run in the browser.");
      }

      const url = new URL(basePath, window.location.origin);
      inputs.forEach((input) => {
        url.searchParams.set(input.id, String(values[input.id]));
      });

      Object.entries(extraParams).forEach(([key, val]) => {
        if (val === undefined || val === "") {
          return;
        }
        url.searchParams.set(key, String(val));
      });

      if (hash) {
        url.hash = hash;
      }

      return url.toString();
    },
    [inputs, values]
  );

  return {
    values,
    setValue,
    resetValues,
    appliedFromUrl,
    buildUrl,
  };
}
